import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Play, X, ArrowRight } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: "bridal-makeup", label: "Bridal Makeup", color: "from-pink-500 to-rose-500" },
  { value: "reception-makeup", label: "Reception Makeup", color: "from-purple-500 to-indigo-500" },
  { value: "engagement-makeup", label: "Engagement Makeup", color: "from-amber-500 to-orange-500" },
  { value: "glam-party-makeup", label: "Glam & Party Makeup", color: "from-teal-500 to-cyan-500" }
];

export default function BridalPortfolioPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  
  const [categoryPreviews, setCategoryPreviews] = useState([]);
  const [items, setItems] = useState([]);
  const [displayedItems, setDisplayedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(categoryParam);
  const [modalItem, setModalItem] = useState(null);
  
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const checkMobile = () => {
      setItemsPerPage(window.innerWidth < 768 ? 6 : 20);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (selectedCategory) {
      fetchItems();
    } else {
      fetchCategoryPreviews();
    }
  }, [selectedCategory]);

  useEffect(() => {
    const endIndex = currentPage * itemsPerPage;
    setDisplayedItems(items.slice(0, endIndex));
  }, [items, currentPage, itemsPerPage]);

  const fetchCategoryPreviews = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/bridal-portfolio/categories-with-preview`);
      const data = await response.json();
      setCategoryPreviews(data.categories || []);
    } catch (error) {
      console.error("Error fetching category previews:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/portfolio/bridal-portfolio?category=${selectedCategory}`);
      const data = await response.json();
      setItems(data.items || []);
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching portfolio items:", error);
    } finally {
      setLoading(false);
    }
  };

  const selectCategory = (cat) => {
    setSelectedCategory(cat);
    setSearchParams({ category: cat });
  };

  const goBack = () => {
    if (selectedCategory) {
      setSelectedCategory(null);
      setSearchParams({});
      setItems([]);
    } else {
      navigate(-1);
    }
  };

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const hasMore = displayedItems.length < items.length;

  const getMediaUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    return `${BACKEND_URL}${url}`;
  };

  const getCategoryInfo = (catValue) => {
    return CATEGORIES.find(c => c.value === catValue) || { label: catValue, color: "from-gray-500 to-gray-600" };
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={goBack}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <h1 className="text-xl font-bold bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
            MUAS Academy
          </h1>
        </div>
      </header>

      {/* Title Section */}
      <section className="py-12 text-center">
        <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-pink-600 to-purple-600 bg-clip-text text-transparent">
          {selectedCategory ? getCategoryInfo(selectedCategory).label : "Real Bride Portfolio"}
        </h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto px-4">
          {selectedCategory 
            ? "Explore our stunning bridal transformations" 
            : "Choose a category to explore our bridal makeup work"}
        </p>
      </section>

      {/* Content */}
      <section className="container mx-auto px-4 pb-16">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-600"></div>
          </div>
        ) : !selectedCategory ? (
          /* Category Cards - 2x2 Grid - Only show categories with content */
          <div className="grid grid-cols-2 gap-4 md:gap-6 max-w-4xl mx-auto">
            {CATEGORIES.filter((cat) => {
              const preview = categoryPreviews.find(p => p.category === cat.value);
              return preview && preview.count > 0;
            }).map((cat) => {
              const preview = categoryPreviews.find(p => p.category === cat.value);
              return (
                <div
                  key={cat.value}
                  onClick={() => selectCategory(cat.value)}
                  className="relative aspect-square rounded-2xl overflow-hidden shadow-xl cursor-pointer group"
                >
                  {/* Background Image */}
                  {preview?.preview ? (
                    <img 
                      src={getMediaUrl(preview.preview)} 
                      alt={cat.label}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  ) : (
                    <div className={`absolute inset-0 bg-gradient-to-br ${cat.color}`} />
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                  
                  {/* Content */}
                  <div className="absolute inset-0 flex flex-col items-center justify-end p-4 md:p-6 text-white">
                    <h3 className="text-lg md:text-2xl font-bold text-center mb-2">{cat.label}</h3>
                    {preview?.count > 0 && (
                      <p className="text-sm text-white/80">{preview.count} items</p>
                    )}
                    <div className="mt-3 flex items-center gap-1 text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                      <span>View All</span>
                      <ArrowRight className="w-4 h-4" />
                    </div>
                  </div>
                </div>
              );
            })}
            {/* Show message if no categories have content */}
            {CATEGORIES.filter((cat) => {
              const preview = categoryPreviews.find(p => p.category === cat.value);
              return preview && preview.count > 0;
            }).length === 0 && (
              <div className="col-span-2 text-center py-20">
                <p className="text-gray-500 text-lg">No portfolio items yet. Check back soon!</p>
              </div>
            )}
          </div>
        ) : displayedItems.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-gray-500 text-lg">No items yet. Check back soon!</p>
          </div>
        ) : (
          <>
            {/* Gallery Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {displayedItems.map((item) => (
                <div
                  key={item.id}
                  className="relative aspect-square rounded-xl overflow-hidden shadow-lg cursor-pointer group"
                  onClick={() => setModalItem(item)}
                >
                  {item.media_type === "video" ? (
                    <>
                      <video
                        src={getMediaUrl(item.thumbnail || item.url)}
                        className="w-full h-full object-cover"
                        muted
                        playsInline
                      />
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/50 transition-all">
                        <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center">
                          <Play className="w-6 h-6 text-pink-600 ml-1" fill="currentColor" />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <img
                        src={getMediaUrl(item.url)}
                        alt=""
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all" />
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Load More Button */}
            {hasMore && (
              <div className="text-center mt-10">
                <Button
                  onClick={loadMore}
                  className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-600 hover:to-purple-600 text-white px-8 py-3 rounded-full"
                >
                  Load More
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      {/* Modal */}
      {modalItem && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
          onClick={() => setModalItem(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white hover:text-gray-300"
            onClick={() => setModalItem(null)}
          >
            <X className="w-8 h-8" />
          </button>
          
          <div className="max-w-4xl max-h-[90vh] w-full" onClick={(e) => e.stopPropagation()}>
            {modalItem.media_type === "video" ? (
              <video
                src={getMediaUrl(modalItem.url)}
                className="w-full h-full max-h-[90vh] object-contain"
                controls
                autoPlay
              />
            ) : (
              <img
                src={getMediaUrl(modalItem.url)}
                alt=""
                className="w-full h-full max-h-[90vh] object-contain"
              />
            )}
          </div>
        </div>
      )}

      {/* Footer */}
      <Footer />
    </div>
  );
}
